import { useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigationType } from 'react-router-dom'

/**
 * Capa RAÍZ de la tabla de rutas: no pinta nada, solo reinicia el
 * desplazamiento cuando se cambia de ruta (goal "En el portal, la página nueva
 * arranca arriba").
 *
 * React Router NO reinicia el scroll al navegar: en una SPA no hay recarga que
 * lo ponga a cero, así que quien pulsaba «Ver más actualizaciones» —un enlace
 * que vive al pie de la portada— aterrizaba en `/actualizaciones` a media
 * página y creía la página rota. El arreglo va aquí, montado UNA vez, y no en
 * la página de actualizaciones: el defecto era de toda la navegación del
 * portal.
 *
 * Tres cosas que hay que conservar al tocar este fichero:
 *
 * 1. 🚨 **`behavior: 'instant'` EXPLÍCITO, no basta con omitirlo.**
 *    `styles/globals.css` declara `html { scroll-behavior: smooth }` global, así
 *    que un `window.scrollTo(0, 0)` a secas HEREDA la animación de la hoja de
 *    estilos y se ve el barrido desde el pie de la página anterior — el defecto
 *    visual que este arreglo viene a quitar. jsdom no aplica CSS: sin el espía
 *    del test, esto solo se vería en producción.
 * 2. **Se reinicia al cambiar la RUTA, no la URL.** El guardián de
 *    `ultimaRuta` es lo que protege el filtro del histórico (`?repo=`, con
 *    `replace`) y cualquier otro estado en los parámetros de búsqueda: cambiar
 *    de burbuja no debe mover al visitante de sitio. Ojo: `navigationType`
 *    cambia (PUSH → REPLACE) al filtrar, así que sin ese guardián el efecto se
 *    volvería a disparar aunque la ruta sea la misma.
 * 3. **Las anclas y el botón atrás se respetan.** Una URL con fragmento pide
 *    una sección, no el principio; y en una navegación POP (atrás/adelante) la
 *    posición la restituye el navegador, que guarda una por entrada del
 *    historial — nadie la pisa desde aquí. El primer montaje también es POP,
 *    y ahí no hay nada que reiniciar.
 */
export function RootLayout() {
  const { pathname, hash } = useLocation()
  const navigationType = useNavigationType()
  const ultimaRuta = useRef<string | null>(null)

  useEffect(() => {
    const cambioDeRuta = ultimaRuta.current !== pathname
    ultimaRuta.current = pathname

    if (!cambioDeRuta) return
    if (navigationType === 'POP') return
    if (hash) return

    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [pathname, hash, navigationType])

  return <Outlet />
}
